import React from 'react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';

export default function TermsOfService() {
  return (
    <div className="relative min-h-screen bg-rax-bg overflow-x-hidden">
      <Navbar />
      <main className="relative z-10 mx-auto max-w-4xl px-6 pb-24 pt-32">
        <h1 className="mb-2 font-heading text-3xl font-bold text-rax-heading">Terms of Service</h1>
        <p className="mb-10 text-sm text-rax-body/60">Last updated: March 22, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed text-rax-body">

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and RAX Protocol Foundation
              ("Company," "we," "us," or "our"), governing your access to and use of the RAX Protocol platform,
              including the website at raxprotocol.xyz, its subdomains, and any associated software, smart contracts,
              or services (collectively, the "Service").
            </p>
            <p className="mt-3">
              By accessing or using the Service, you confirm that you have read, understood, and agree to be bound
              by these Terms. If you do not agree, you must not access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">2. Description of Service</h2>
            <p>
              RAX Protocol is an AI-powered, non-custodial DeFi vault protocol designed to autonomously manage,
              rebalance, and optimize user capital across DeFi strategies. The Service allows users to deposit
              capital into AI-managed vaults that allocate across DeFi protocols like Aave, Compound,
              Uniswap V3, and Aerodrome. The Service is built on the Base blockchain and utilizes the ERC-4626
              vault standard.
            </p>
            <p className="mt-3">
              RAX Protocol does not take custody of your assets. All interactions with the protocol occur through
              non-custodial smart contracts. You retain sole control of your private keys and wallet at all times.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">3. Eligibility</h2>
            <p>To use the Service, you must:</p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>Be at least 18 years of age;</li>
              <li>Have the legal capacity to enter into a binding contract in your jurisdiction;</li>
              <li>
                Not be a resident of, or located in, any jurisdiction where use of decentralized finance
                protocols or digital assets is prohibited or restricted, including but not limited to the
                United States (where applicable regulatory restrictions apply), Iran, North Korea, Syria,
                Cuba, or any other jurisdiction subject to comprehensive sanctions;
              </li>
              <li>Not be on any sanctions list maintained by OFAC or equivalent bodies.</li>
            </ul>
            <p className="mt-3">
              By using the Service, you represent and warrant that you meet all eligibility requirements.
              We reserve the right to restrict access at any time at our discretion.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">4. Non-Custodial Nature &amp; User Responsibility</h2>
            <p>
              RAX Protocol is a non-custodial protocol. This means:
            </p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>We do not hold, control, or have access to your funds or private keys;</li>
              <li>You are solely responsible for the security of your wallet and credentials;</li>
              <li>All vault management and capital allocation decisions are executed autonomously by smart contracts and AI systems;</li>
              <li>You bear all risk associated with depositing capital into DeFi protocols;</li>
              <li>Lost private keys, compromised wallets, or user errors cannot be reversed by us.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">5. Risks</h2>
            <p>
              Use of the Service involves significant risks, including but not limited to:
            </p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li><strong className="text-rax-heading">Smart Contract Risk:</strong> Bugs or vulnerabilities in smart contracts may result in permanent loss of funds;</li>
              <li><strong className="text-rax-heading">AI and Algorithmic Risk:</strong> AI-driven vault management may produce suboptimal outcomes or losses;</li>
              <li><strong className="text-rax-heading">Market Risk:</strong> Digital asset values are highly volatile and may decrease significantly;</li>
              <li><strong className="text-rax-heading">Liquidity Risk:</strong> You may be unable to withdraw funds during periods of low liquidity;</li>
              <li><strong className="text-rax-heading">Protocol Risk:</strong> Third-party DeFi protocols (Aave, Compound, Uniswap V3, Aerodrome) may be exploited, paused, or fail;</li>
              <li><strong className="text-rax-heading">Regulatory Risk:</strong> Changes in law or regulation may affect the availability or legality of the Service in your jurisdiction;</li>
              <li><strong className="text-rax-heading">Network Risk:</strong> Blockchain congestion or failures may delay or prevent transactions.</li>
            </ul>
            <p className="mt-3">
              You acknowledge these risks and agree that you are solely responsible for any losses incurred.
              Never deposit more than you can afford to lose entirely.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">6. Not Financial Advice</h2>
            <p>
              Nothing on raxprotocol.xyz or in the Service constitutes financial, investment, legal, or tax advice.
              All information is provided for informational purposes only. You should consult qualified
              professionals before making any financial decisions. Past performance of vault strategies is not
              indicative of future results.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">7. Prohibited Activities</h2>
            <p>You agree not to:</p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>Use the Service for any unlawful purpose or in violation of these Terms;</li>
              <li>Attempt to circumvent any access restrictions or security measures;</li>
              <li>Use automated bots, scrapers, or scripts to access the Service without authorization;</li>
              <li>Engage in market manipulation, wash trading, or other manipulative practices;</li>
              <li>Use the Service to launder money or finance illegal activities;</li>
              <li>Reverse engineer, decompile, or attempt to extract source code (other than open-source contracts);</li>
              <li>Interfere with or disrupt the integrity or performance of the Service or its underlying infrastructure.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">8. Intellectual Property</h2>
            <p>
              All content, trademarks, logos, software, and materials on raxprotocol.xyz are the property of
              RAX Protocol Foundation or its licensors and are protected by applicable intellectual property laws.
              You are granted a limited, non-exclusive, non-transferable license to access and use the Service
              for personal, non-commercial purposes. All other rights are reserved.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">9. Third-Party Services</h2>
            <p>
              The Service integrates with third-party DeFi protocols and services. We are not responsible for
              the operation, security, or availability of these third-party services. Your interactions with
              them are governed by their own terms and policies. Links to external sites do not constitute
              endorsement.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">10. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
              IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR
              SECURE. WE MAKE NO REPRESENTATIONS REGARDING THE ACCURACY OR COMPLETENESS OF ANY INFORMATION
              ON THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, RAX PROTOCOL FOUNDATION AND ITS AFFILIATES, OFFICERS, DIRECTORS,
              EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
              OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, LOSS OF FUNDS, DATA LOSS, OR LOSS OF GOODWILL,
              ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY
              OF SUCH DAMAGES.
            </p>
            <p className="mt-3">
              OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED
              THE GREATER OF (A) $100 USD OR (B) THE AMOUNT OF FEES PAID BY YOU TO US IN THE THREE MONTHS
              PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless RAX Protocol Foundation and its affiliates, officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses,
              including reasonable legal fees, arising from your use of the Service, your violation of these
              Terms, or your violation of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">13. Modifications to the Service and Terms</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue the Service at any time without notice.
              We may also update these Terms at any time. Continued use of the Service after changes constitute
              your acceptance of the updated Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">14. Governing Law &amp; Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of Switzerland, without regard to conflict of
              law principles. Any dispute arising from or relating to these Terms or the Service shall be
              resolved exclusively through binding arbitration in Zürich, Switzerland, under the rules of
              the Swiss Arbitration Centre. You waive any right to participate in class action proceedings.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">15. Severability &amp; Entire Agreement</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining provisions will
              continue in full force and effect. These Terms, together with our Privacy Policy, constitute
              the entire agreement between you and RAX Protocol Foundation regarding the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">16. Contact</h2>
            <p>
              For questions or concerns regarding these Terms, please contact us at:{' '}
              <a href="mailto:info@raxprotocol.xyz" className="text-rax-cyan hover:text-rax-cyan/80 transition-colors">
                info@raxprotocol.xyz
              </a>
            </p>
            <p className="mt-2">RAX Protocol Foundation, Bahnhofstrasse 42, 8001 Zürich, Switzerland.</p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
